import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import ProductVariantSelector from './ProductVariantSelector';
import TargetPriceManager from './TargetPriceManager';
import promotionService from '../../services/marketingService';
import {
  Promotion,
  PromotionType,
  PromotionSchemeType,
  InvoicePage,
  MarketingProductVariant,
  TargetType,
  PromotionTarget,
  BuyXGetYScheme,
  ProductDiscountScheme,
  TaxDiscountScheme,
  OrderDiscountScheme,
  ComboScheme,
  ComboOption,
  TargetPriceScheme,
  TargetPriceItem
} from '../../types/marketingTypes';
import * as productService from '../../services/productService';

// الواجهة للنموذج
interface CampaignFormProps {
  isEdit?: boolean;
}

// واجهة القيم الأولية للنموذج
interface FormValues {
  name: string;
  description: string;
  promotionType: PromotionType;
  schemeType: PromotionSchemeType;
  startDate: string;
  endDate: string;
  code: string;
  maxUses: number;
  applicableInvoicePages: string[];
  isActive: boolean;

  // حقول اشتر X واحصل على Y
  primaryTargetType: TargetType;
  primaryTargetId: string;
  primaryTargetQuantity: number;
  secondaryTargetType: TargetType;
  secondaryTargetId: string;
  secondaryTargetQuantity: number;
  secondaryDiscount: number;

  // حقول خصم على المنتجات
  productDiscountType: 'percentage' | 'fixed';
  productDiscountValue: number;
  productDiscountMaxDiscount: number;

  // حقول خصم على الضريبة
  taxDiscountPercentage: number;
  taxDiscountMaxDiscount: number;
  taxDiscountTargets: PromotionTarget[];

  // حقول خصم على الطلب
  orderTargets: PromotionTarget[];
  orderMinAmount: number;
  orderMinTargetAmount: number;
  orderDiscountType: 'percentage' | 'fixed';
  orderDiscountValue: number;
  orderDiscountMaxDiscount: number;

  // حقول الكومبو
  comboOptions: ComboOption[];

  // حقول السعر المستهدف
  targetPriceItems: TargetPriceItem[];
  showOriginalPrice: boolean;
}

// نموذج Yup للتحقق من صحة البيانات
const validationSchema = Yup.object({
  name: Yup.string().required('اسم الحملة مطلوب'),
  description: Yup.string(),
  promotionType: Yup.string().required('نوع الحملة مطلوب'),
  schemeType: Yup.string().required('نوع آلية الحملة مطلوب'),
  startDate: Yup.string().required('تاريخ بداية الحملة مطلوب'),
  endDate: Yup.string().required('تاريخ نهاية الحملة مطلوب'),
  applicableInvoicePages: Yup.array()
    .of(Yup.string())
    .min(1, 'يجب اختيار صفحة فاتورة واحدة على الأقل'),
  code: Yup.string().when('promotionType', {
    is: (value: string) => value === PromotionType.COUPON,
    then: () => Yup.string().required('رمز الكوبون مطلوب')
  }),
  maxUses: Yup.number().when('promotionType', {
    is: (value: string) => value === PromotionType.COUPON,
    then: () => Yup.number()
      .min(1, 'يجب أن يكون الحد الأقصى للاستخدام أكبر من صفر')
      .required('الحد الأقصى للاستخدام مطلوب')
  }),
  taxDiscountTargets: Yup.array()
});

// تحويل التاريخ إلى تنسيق ISO string للاستخدام في حقول التاريخ
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// القيم الافتراضية للنموذج
const initialValues: FormValues = {
  name: '',
  description: '',
  promotionType: PromotionType.AUTOMATIC,
  schemeType: PromotionSchemeType.PRODUCT_DISCOUNT,
  startDate: formatDateForInput(new Date()),
  endDate: formatDateForInput(new Date(new Date().setMonth(new Date().getMonth() + 1))),
  code: '',
  maxUses: 100,
  applicableInvoicePages: [],
  isActive: true,

  // حقول اشتر X واحصل على Y
  primaryTargetType: TargetType.PRODUCT_VARIANT,
  primaryTargetId: '',
  primaryTargetQuantity: 1,
  secondaryTargetType: TargetType.PRODUCT_VARIANT,
  secondaryTargetId: '',
  secondaryTargetQuantity: 1,
  secondaryDiscount: 50,

  // حقول خصم على المنتجات
  productDiscountType: 'percentage',
  productDiscountValue: 10,
  productDiscountMaxDiscount: 0,

  // حقول خصم على الضريبة
  taxDiscountPercentage: 50,
  taxDiscountMaxDiscount: 0,
  taxDiscountTargets: [],

  // حقول خصم على الطلب
  orderTargets: [],
  orderMinAmount: 100,
  orderMinTargetAmount: 0,
  orderDiscountType: 'percentage',
  orderDiscountValue: 10,
  orderDiscountMaxDiscount: 0,

  // حقول الكومبو
  comboOptions: [],

  // حقول السعر المستهدف
  targetPriceItems: [],
  showOriginalPrice: true
};

// مكون نموذج الحملة
const CampaignForm: React.FC<CampaignFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [error, setError] = useState<string>('');
  const [campaign, setCampaign] = useState<Promotion | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [availableInvoicePages, setAvailableInvoicePages] = useState<InvoicePage[]>([]);
  const [selectedPrimaryVariants, setSelectedPrimaryVariants] = useState<MarketingProductVariant[]>([]);
  const [selectedSecondaryVariants, setSelectedSecondaryVariants] = useState<MarketingProductVariant[]>([]);
  const [selectedComboVariants, setSelectedComboVariants] = useState<MarketingProductVariant[]>([]);
  const [selectedTargetVariants, setSelectedTargetVariants] = useState<MarketingProductVariant[]>([]);
  const [targetPriceItems, setTargetPriceItems] = useState<TargetPriceItem[]>([]);
  const [selectedProductDiscountVariants, setSelectedProductDiscountVariants] = useState<MarketingProductVariant[]>([]);
  const [selectedTaxDiscountVariants, setSelectedTaxDiscountVariants] = useState<MarketingProductVariant[]>([]);
  const [selectedOrderDiscountVariants, setSelectedOrderDiscountVariants] = useState<MarketingProductVariant[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedOrderDiscountCategories, setSelectedOrderDiscountCategories] = useState<string[]>([]);
  const [selectedProductDiscountCategories, setSelectedProductDiscountCategories] = useState<string[]>([]);
  const [selectedTaxDiscountCategories, setSelectedTaxDiscountCategories] = useState<string[]>([]);
  const [selectedPrimaryCategories, setSelectedPrimaryCategories] = useState<string[]>([]);
  const [selectedSecondaryCategories, setSelectedSecondaryCategories] = useState<string[]>([]);
  const [selectedComboCategories, setSelectedComboCategories] = useState<string[]>([]);
  const [selectedTargetCategories, setSelectedTargetCategories] = useState<string[]>([]);
  const [variantSelectorOpen, setVariantSelectorOpen] = useState<boolean>(false);
  const [variantSelectorType, setVariantSelectorType] = useState<'primary' | 'secondary' | 'combo' | 'target' | 'product_discount' | 'tax_discount' | 'order_discount'>('primary');
  const [comboOptions, setComboOptions] = useState<ComboOption[]>([]);

  // جلب البيانات عند التحميل
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // جلب المنتجات
        const productsData = await productService.getProducts(1, 100);
        setProducts(productsData.products || []);
        
        // جلب صفحات الفواتير المتاحة
        const invoicePages = await promotionService.getAllInvoicePages();
        setAvailableInvoicePages(invoicePages);
        
        // إذا كانت الصفحة للتعديل، جلب بيانات الحملة
        if (isEdit && id) {
          console.log('Fetching campaign data for ID:', id);
          const campaignData = await promotionService.getPromotionById(id);
          console.log('Campaign data received:', campaignData);
          setCampaign(campaignData);
          
          // تحميل المنتجات المختارة حسب نوع الآلية
          if (campaignData.productDiscountScheme?.targets) {
            try {
              const variants = await Promise.all(
                campaignData.productDiscountScheme.targets
                  .filter(target => target.targetType === 'product_variant')
                  .map(async target => {
                    try {
                      return await promotionService.getVariantById(target.targetId);
                    } catch (err) {
                      console.warn('Failed to load variant:', target.targetId, err);
                      return null;
                    }
                  })
              );
              setSelectedProductDiscountVariants(variants.filter(v => v !== null));
              
              // تحميل التصنيفات
              const categories = campaignData.productDiscountScheme.targets
                .filter(target => target.targetType === 'category')
                .map(target => target.targetId);
              setSelectedProductDiscountCategories(categories);
            } catch (err) {
              console.error('Error loading product discount variants:', err);
            }
          }
          
          if (campaignData.taxDiscountScheme?.targets) {
            try {
              const variants = await Promise.all(
                campaignData.taxDiscountScheme.targets
                  .filter(target => target.targetType === 'product_variant')
                  .map(async target => {
                    try {
                      return await promotionService.getVariantById(target.targetId);
                    } catch (err) {
                      console.warn('Failed to load variant:', target.targetId, err);
                      return null;
                    }
                  })
              );
              setSelectedTaxDiscountVariants(variants.filter(v => v !== null));
              
              // تحميل التصنيفات
              const categories = campaignData.taxDiscountScheme.targets
                .filter(target => target.targetType === 'category')
                .map(target => target.targetId);
              setSelectedTaxDiscountCategories(categories);
            } catch (err) {
              console.error('Error loading tax discount variants:', err);
            }
          }
          
          if (campaignData.orderDiscountScheme?.targets) {
            try {
              const variants = await Promise.all(
                campaignData.orderDiscountScheme.targets
                  .filter(target => target.targetType === 'product_variant')
                  .map(async target => {
                    try {
                      return await promotionService.getVariantById(target.targetId);
                    } catch (err) {
                      console.warn('Failed to load variant:', target.targetId, err);
                      return null;
                    }
                  })
              );
              setSelectedOrderDiscountVariants(variants.filter(v => v !== null));
              
              // تحميل التصنيفات
              const categories = campaignData.orderDiscountScheme.targets
                .filter(target => target.targetType === 'category')
                .map(target => target.targetId);
              setSelectedOrderDiscountCategories(categories);
            } catch (err) {
              console.error('Error loading order discount variants:', err);
            }
          }
          
          if (campaignData.buyXGetYScheme) {
            try {
              // تحميل الهدف الأساسي
              if (campaignData.buyXGetYScheme.primaryTarget?.targetType === 'product_variant') {
                const variant = await promotionService.getVariantById(campaignData.buyXGetYScheme.primaryTarget.targetId);
                if (variant) setSelectedPrimaryVariants([variant]);
              } else if (campaignData.buyXGetYScheme.primaryTarget?.targetType === 'category') {
                setSelectedPrimaryCategories([campaignData.buyXGetYScheme.primaryTarget.targetId]);
              }
              
              // تحميل الهدف الثانوي
              if (campaignData.buyXGetYScheme.secondaryTarget?.targetType === 'product_variant') {
                const variant = await promotionService.getVariantById(campaignData.buyXGetYScheme.secondaryTarget.targetId);
                if (variant) setSelectedSecondaryVariants([variant]);
              } else if (campaignData.buyXGetYScheme.secondaryTarget?.targetType === 'category') {
                setSelectedSecondaryCategories([campaignData.buyXGetYScheme.secondaryTarget.targetId]);
              }
            } catch (err) {
              console.error('Error loading buy X get Y variants:', err);
            }
          }
          
          if (campaignData.comboScheme?.products) {
            try {
              const variants = await Promise.all(
                campaignData.comboScheme.products
                  .filter(product => product.target.targetType === 'product_variant')
                  .map(async product => {
                    try {
                      return await promotionService.getVariantById(product.target.targetId);
                    } catch (err) {
                      console.warn('Failed to load variant:', product.target.targetId, err);
                      return null;
                    }
                  })
              );
              setSelectedComboVariants(variants.filter(v => v !== null));
              
              // تحميل التصنيفات المختارة
              const categories = campaignData.comboScheme.products
                .filter(product => product.target.targetType === 'category')
                .map(product => product.target.targetId);
              setSelectedComboCategories(categories);
            } catch (err) {
              console.error('Error loading combo variants:', err);
            }
          }
          
          if (campaignData.targetPriceScheme?.targets) {
            try {
              const variants = await Promise.all(
                campaignData.targetPriceScheme.targets
                  .filter(target => target.target.targetType === 'product_variant')
                  .map(async target => {
                    try {
                      return await promotionService.getVariantById(target.target.targetId);
                    } catch (err) {
                      console.warn('Failed to load variant:', target.target.targetId, err);
                      return null;
                    }
                  })
              );
              setSelectedTargetVariants(variants.filter(v => v !== null));
            } catch (err) {
              console.error('Error loading target price variants:', err);
            }
          }
          
          // تحميل بيانات الكومبو والأسعار المستهدفة
          if (campaignData.comboScheme?.comboOptions) {
            setComboOptions(campaignData.comboScheme.comboOptions);
          }
          
          if (campaignData.targetPriceScheme?.targets) {
            setTargetPriceItems(campaignData.targetPriceScheme.targets);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError('حدث خطأ أثناء تحميل البيانات: ' + errorMessage);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isEdit, id]);

  // تحويل بيانات الحملة إلى قيم النموذج
  const mapCampaignToFormValues = (campaign: Promotion): FormValues => {
    const baseValues: FormValues = {
      ...initialValues,
      name: campaign.name || '',
      description: campaign.description || '',
      promotionType: campaign.promotionType,
      schemeType: campaign.schemeType,
      startDate: formatDateForInput(new Date(campaign.startDate)),
      endDate: formatDateForInput(new Date(campaign.endDate)),
      code: campaign.code || '',
      maxUses: campaign.maxUses || 100,
      applicableInvoicePages: campaign.applicableInvoicePages || [],
      isActive: campaign.isActive !== undefined ? campaign.isActive : true
    };

    // تحميل بيانات الآلية المحددة
    if (campaign.buyXGetYScheme) {
      baseValues.primaryTargetType = campaign.buyXGetYScheme.primaryTarget?.targetType || TargetType.PRODUCT_VARIANT;
      baseValues.primaryTargetId = campaign.buyXGetYScheme.primaryTarget?.targetId || '';
      baseValues.primaryTargetQuantity = campaign.buyXGetYScheme.primaryTarget?.quantity || 1;
      baseValues.secondaryTargetType = campaign.buyXGetYScheme.secondaryTarget?.targetType || TargetType.PRODUCT_VARIANT;
      baseValues.secondaryTargetId = campaign.buyXGetYScheme.secondaryTarget?.targetId || '';
      baseValues.secondaryTargetQuantity = campaign.buyXGetYScheme.secondaryTarget?.quantity || 1;
      baseValues.secondaryDiscount = campaign.buyXGetYScheme.secondaryDiscount || 50;
    }

    if (campaign.productDiscountScheme) {
      baseValues.productDiscountType = campaign.productDiscountScheme.discountType || 'percentage';
      baseValues.productDiscountValue = campaign.productDiscountScheme.discountValue || 10;
      baseValues.productDiscountMaxDiscount = campaign.productDiscountScheme.maxDiscount || 0;
    }

    if (campaign.taxDiscountScheme) {
      baseValues.taxDiscountPercentage = campaign.taxDiscountScheme.discountPercentage || 50;
      baseValues.taxDiscountMaxDiscount = campaign.taxDiscountScheme.maxDiscount || 0;
      baseValues.taxDiscountTargets = campaign.taxDiscountScheme.targets || [];
    }

    if (campaign.orderDiscountScheme) {
      baseValues.orderTargets = campaign.orderDiscountScheme.targets || [];
      baseValues.orderMinAmount = campaign.orderDiscountScheme.minOrderAmount || 100;
      baseValues.orderMinTargetAmount = campaign.orderDiscountScheme.minTargetAmount || 0;
      baseValues.orderDiscountType = campaign.orderDiscountScheme.discountType || 'percentage';
      baseValues.orderDiscountValue = campaign.orderDiscountScheme.discountValue || 10;
      baseValues.orderDiscountMaxDiscount = campaign.orderDiscountScheme.maxDiscount || 0;
    }

    if (campaign.comboScheme) {
      baseValues.comboOptions = campaign.comboScheme.comboOptions || [];
    }

    if (campaign.targetPriceScheme) {
      baseValues.targetPriceItems = campaign.targetPriceScheme.targets || [];
      baseValues.showOriginalPrice = campaign.targetPriceScheme.showOriginalPrice !== undefined ? campaign.targetPriceScheme.showOriginalPrice : true;
    }

    return baseValues;
  };

  // معالجة تقديم النموذج
  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    try {
      const campaignData: any = {
        name: values.name,
        description: values.description,
        promotionType: values.promotionType,
        schemeType: values.schemeType,
        startDate: new Date(values.startDate),
        endDate: new Date(values.endDate),
        code: values.promotionType === PromotionType.COUPON ? values.code : undefined,
        maxUses: values.promotionType === PromotionType.COUPON ? values.maxUses : undefined,
        currentUses: campaign?.currentUses || 0,
        applicableInvoicePages: values.applicableInvoicePages,
        isActive: values.isActive
      };

      // إضافة بيانات الآلية المحددة حسب نوع الحملة
      switch (values.schemeType) {
        case PromotionSchemeType.BUY_X_GET_Y:
          // التحقق من وجود هدف أساسي وثانوي
          const hasPrimaryTarget = selectedPrimaryVariants.length > 0 || selectedPrimaryCategories.length > 0;
          const hasSecondaryTarget = selectedSecondaryVariants.length > 0 || selectedSecondaryCategories.length > 0;

          if (!hasPrimaryTarget || !hasSecondaryTarget) {
            setError('يجب اختيار الهدف الأساسي والثانوي لآلية اشتر X واحصل على Y (منتج أو تصنيف)');
            helpers.setSubmitting(false);
            return;
          }

          // تحديد الهدف الأساسي
          let primaryTarget;
          if (selectedPrimaryVariants.length > 0) {
            primaryTarget = {
              targetType: 'product_variant',
              targetId: selectedPrimaryVariants[0]._id,
              quantity: values.primaryTargetQuantity
            };
          } else if (selectedPrimaryCategories.length > 0) {
            primaryTarget = {
              targetType: 'category',
              targetId: selectedPrimaryCategories[0],
              quantity: values.primaryTargetQuantity
            };
          }

          // تحديد الهدف الثانوي
          let secondaryTarget;
          if (selectedSecondaryVariants.length > 0) {
            secondaryTarget = {
              targetType: 'product_variant',
              targetId: selectedSecondaryVariants[0]._id,
              quantity: values.secondaryTargetQuantity
            };
          } else if (selectedSecondaryCategories.length > 0) {
            secondaryTarget = {
              targetType: 'category',
              targetId: selectedSecondaryCategories[0],
              quantity: values.secondaryTargetQuantity
            };
          }

          campaignData.buyXGetYScheme = {
            primaryTarget,
            secondaryTarget,
            secondaryDiscount: values.secondaryDiscount,
            // إضافة التصنيفات المنفصلة
            primaryCategories: selectedPrimaryCategories,
            secondaryCategories: selectedSecondaryCategories
          };
          break;

        case PromotionSchemeType.PRODUCT_DISCOUNT:
          if (selectedProductDiscountVariants.length === 0 && selectedProductDiscountCategories.length === 0) {
            setError('يجب اختيار منتج واحد على الأقل أو تصنيف واحد على الأقل لآلية خصم على المنتجات');
            helpers.setSubmitting(false);
            return;
          }
          
          const productDiscountTargets = [
            ...selectedProductDiscountVariants.map(variant => ({
              targetType: 'product_variant',
              targetId: variant._id,
              quantity: 1
            })),
            ...selectedProductDiscountCategories.map(categoryId => ({
              targetType: 'category',
              targetId: categoryId,
              quantity: 1
            }))
          ];
          
          campaignData.productDiscountScheme = {
            targets: productDiscountTargets,
            discountType: values.productDiscountType,
            discountValue: values.productDiscountValue,
            maxDiscount: values.productDiscountMaxDiscount || undefined
          };
          break;

        case PromotionSchemeType.TAX_DISCOUNT:
          if (selectedTaxDiscountVariants.length === 0 && selectedTaxDiscountCategories.length === 0) {
            setError('يجب اختيار منتج واحد على الأقل أو تصنيف واحد على الأقل لآلية خصم على الضريبة');
            helpers.setSubmitting(false);
            return;
          }
          
          const taxDiscountTargets = [
            ...selectedTaxDiscountVariants.map(variant => ({
              targetType: 'product_variant',
              targetId: variant._id,
              quantity: 1
            })),
            ...selectedTaxDiscountCategories.map(categoryId => ({
              targetType: 'category',
              targetId: categoryId,
              quantity: 1
            }))
          ];
          
          campaignData.taxDiscountScheme = {
            targets: taxDiscountTargets,
            discountPercentage: values.taxDiscountPercentage,
            maxDiscount: values.taxDiscountMaxDiscount || undefined
          };
          break;

        case PromotionSchemeType.ORDER_DISCOUNT:
          const orderDiscountTargets = [
            ...selectedOrderDiscountVariants.map(variant => ({
              targetType: 'product_variant',
              targetId: variant._id,
              quantity: 1
            })),
            ...selectedOrderDiscountCategories.map(categoryId => ({
              targetType: 'category',
              targetId: categoryId,
              quantity: 1
            }))
          ];
          
          campaignData.orderDiscountScheme = {
            targets: orderDiscountTargets.length > 0 ? orderDiscountTargets : undefined,
            minOrderAmount: values.orderMinAmount,
            minTargetAmount: orderDiscountTargets.length > 0 ? values.orderMinTargetAmount : undefined,
            discountType: values.orderDiscountType,
            discountValue: values.orderDiscountValue,
            maxDiscount: values.orderDiscountMaxDiscount || undefined
          };
          break;

        case PromotionSchemeType.COMBO:
          if (selectedComboVariants.length === 0 && selectedComboCategories.length === 0) {
            setError('يجب اختيار منتج واحد على الأقل أو تصنيف واحد على الأقل لآلية الكومبو');
            helpers.setSubmitting(false);
            return;
          }
          if (comboOptions.length === 0) {
            setError('يجب إضافة خيار واحد على الأقل للكومبو');
            helpers.setSubmitting(false);
            return;
          }
          
          const comboProducts = [
            ...selectedComboVariants.map(variant => ({
              target: {
                targetType: 'product_variant',
                targetId: variant._id,
                quantity: 1
              },
              quantity: 1
            })),
            ...selectedComboCategories.map(categoryId => ({
              target: {
                targetType: 'category',
                targetId: categoryId,
                quantity: 1
              },
              quantity: 1
            }))
          ];
          
          campaignData.comboScheme = {
            products: comboProducts,
            comboOptions: comboOptions
          };
          break;

        case PromotionSchemeType.TARGET_PRICE:
          if (targetPriceItems.length === 0) {
            setError('يجب تحديد سعر مستهدف لمنتج واحد على الأقل');
            helpers.setSubmitting(false);
            return;
          }
          campaignData.targetPriceScheme = {
            targets: targetPriceItems,
            showOriginalPrice: values.showOriginalPrice
          };
          break;
      }
      
      if (isEdit && id) {
        await promotionService.updatePromotion(id, campaignData);
      } else {
        await promotionService.createPromotion(campaignData);
      }
      
      navigate('/marketing/campaigns');
    } catch (err) {
      console.error('Error saving campaign:', err);
      setError('حدث خطأ أثناء حفظ الحملة');
      helpers.setSubmitting(false);
    }
  };

  // فتح مربع حوار اختيار متغيرات المنتج
  const handleOpenVariantSelector = (type: 'primary' | 'secondary' | 'combo' | 'target' | 'product_discount' | 'tax_discount' | 'order_discount') => {
    setVariantSelectorType(type);
    setVariantSelectorOpen(true);
  };

  // إغلاق مربع حوار اختيار متغيرات المنتج
  const handleCloseVariantSelector = () => {
    setVariantSelectorOpen(false);
  };

  // اختيار متغيرات المنتج
  const handleSelectVariants = (variants: MarketingProductVariant[]) => {
    switch (variantSelectorType) {
      case 'primary':
        setSelectedPrimaryVariants(variants);
        break;
      case 'secondary':
        setSelectedSecondaryVariants(variants);
        break;
      case 'combo':
        setSelectedComboVariants(variants);
        break;
      case 'target':
        setSelectedTargetVariants(variants);
        break;
      case 'product_discount':
        setSelectedProductDiscountVariants(variants);
        break;
      case 'tax_discount':
        setSelectedTaxDiscountVariants(variants);
        break;
      case 'order_discount':
        setSelectedOrderDiscountVariants(variants);
        break;
    }
    handleCloseVariantSelector();
  };

  // إذا كانت الصفحة للتعديل وما زالت البيانات تتحمل
  if (isEdit && loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom align="right">
          {isEdit ? 'تعديل حملة تسويقية' : 'إنشاء حملة تسويقية جديدة'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Formik
          initialValues={campaign ? mapCampaignToFormValues(campaign) : initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            setFieldValue
          }) => (
            <Form>
              <Grid container spacing={3} dir="rtl">
                {/* البيانات الأساسية */}
                <Grid item xs={12}>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>البيانات الأساسية</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            name="name"
                            label="اسم الحملة"
                            fullWidth
                            variant="outlined"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel id="promotion-type-label">نوع الحملة</InputLabel>
                            <Field
                              as={Select}
                              labelId="promotion-type-label"
                              name="promotionType"
                              label="نوع الحملة"
                              onChange={(e: React.ChangeEvent<any>) => {
                                handleChange(e);
                                if (e.target.value === PromotionType.AUTOMATIC) {
                                  setFieldValue('code', '');
                                  setFieldValue('maxUses', 100);
                                }
                              }}
                            >
                              <MenuItem value={PromotionType.AUTOMATIC}>تلقائية</MenuItem>
                              <MenuItem value={PromotionType.COUPON}>كوبون</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <Field
                            as={TextField}
                            name="description"
                            label="وصف الحملة"
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            error={touched.description && Boolean(errors.description)}
                            helperText={touched.description && errors.description}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            name="startDate"
                            label="تاريخ بداية الحملة"
                            type="date"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            error={touched.startDate && Boolean(errors.startDate)}
                            helperText={touched.startDate && errors.startDate}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            name="endDate"
                            label="تاريخ نهاية الحملة"
                            type="date"
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            error={touched.endDate && Boolean(errors.endDate)}
                            helperText={touched.endDate && errors.endDate}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl fullWidth component="fieldset" error={touched.applicableInvoicePages && Boolean(errors.applicableInvoicePages)}>
                            <FormLabel component="legend">صفحات الفواتير المطبقة</FormLabel>
                            <Grid container spacing={1} sx={{ mt: 1 }}>
                              {availableInvoicePages.map((page) => (
                                <Grid item key={page._id}>
                                  <Chip
                                    label={page.name}
                                    onClick={() => {
                                      const isSelected = values.applicableInvoicePages.includes(page._id);
                                      let newPages;
                                      if (isSelected) {
                                        newPages = values.applicableInvoicePages.filter(p => p !== page._id);
                                      } else {
                                        newPages = [...values.applicableInvoicePages, page._id];
                                      }
                                      setFieldValue('applicableInvoicePages', newPages);
                                    }}
                                    color={values.applicableInvoicePages.includes(page._id) ? 'primary' : 'default'}
                                    variant={values.applicableInvoicePages.includes(page._id) ? 'filled' : 'outlined'}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                            {touched.applicableInvoicePages && errors.applicableInvoicePages && (
                              <FormHelperText>{errors.applicableInvoicePages as string}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.isActive}
                                onChange={(e) => setFieldValue('isActive', e.target.checked)}
                                color="primary"
                              />
                            }
                            label="الحملة نشطة"
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                {/* حقول الكوبون */}
                {values.promotionType === PromotionType.COUPON && (
                  <Grid item xs={12}>
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>بيانات الكوبون</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Field
                              as={TextField}
                              name="code"
                              label="رمز الكوبون"
                              fullWidth
                              variant="outlined"
                              error={touched.code && Boolean(errors.code)}
                              helperText={touched.code && errors.code}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Field
                              as={TextField}
                              name="maxUses"
                              label="الحد الأقصى للاستخدام"
                              type="number"
                              fullWidth
                              variant="outlined"
                              error={touched.maxUses && Boolean(errors.maxUses)}
                              helperText={touched.maxUses && errors.maxUses}
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

                {/* آلية الحملة */}
                <Grid item xs={12}>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>آلية الحملة</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel id="scheme-type-label">نوع آلية الحملة</InputLabel>
                            <Field
                              as={Select}
                              labelId="scheme-type-label"
                              name="schemeType"
                              label="نوع آلية الحملة"
                            >
                              <MenuItem value={PromotionSchemeType.BUY_X_GET_Y}>اشتر X واحصل على Y</MenuItem>
                              <MenuItem value={PromotionSchemeType.PRODUCT_DISCOUNT}>خصم على المنتجات</MenuItem>
                              <MenuItem value={PromotionSchemeType.TAX_DISCOUNT}>خصم على الضريبة</MenuItem>
                              <MenuItem value={PromotionSchemeType.ORDER_DISCOUNT}>خصم على الطلب</MenuItem>
                              <MenuItem value={PromotionSchemeType.COMBO}>كومبو (مجموعة منتجات)</MenuItem>
                              <MenuItem value={PromotionSchemeType.TARGET_PRICE}>سعر مستهدف</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>

                        {/* حقول آلية اشتر X واحصل على Y */}
                        {values.schemeType === PromotionSchemeType.BUY_X_GET_Y && (
                          <>
                            <Grid item xs={12}>
                              <Divider>
                                <Chip label="المنتج الأساسي (X)" />
                              </Divider>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => handleOpenVariantSelector('primary')}
                              >
                                اختيار المنتج الأساسي
                              </Button>
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                {selectedPrimaryVariants.length > 0 || selectedPrimaryCategories.length > 0
                                  ? `تم اختيار ${selectedPrimaryVariants.length} منتج${selectedPrimaryCategories.length > 0 ? ` و ${selectedPrimaryCategories.length} تصنيف` : ''}`
                                  : 'لم يتم اختيار أي منتج أو تصنيف'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                name="primaryTargetQuantity"
                                label="الكمية المطلوبة"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{ inputProps: { min: 1 } }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Divider>
                                <Chip label="المنتج الثانوي (Y)" />
                              </Divider>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => handleOpenVariantSelector('secondary')}
                              >
                                اختيار المنتج الثانوي
                              </Button>
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                {selectedSecondaryVariants.length > 0 || selectedSecondaryCategories.length > 0
                                  ? `تم اختيار ${selectedSecondaryVariants.length} منتج${selectedSecondaryCategories.length > 0 ? ` و ${selectedSecondaryCategories.length} تصنيف` : ''}`
                                  : 'لم يتم اختيار أي منتج أو تصنيف'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                name="secondaryTargetQuantity"
                                label="الكمية المطلوبة"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{ inputProps: { min: 1 } }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                as={TextField}
                                name="secondaryDiscount"
                                label="نسبة الخصم على المنتج الثانوي (%)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  inputProps: { min: 0, max: 100 },
                                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                                }}
                              />
                            </Grid>
                          </>
                        )}

                        {/* حقول آلية خصم على المنتجات */}
                        {values.schemeType === PromotionSchemeType.PRODUCT_DISCOUNT && (
                          <>
                            <Grid item xs={12}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => handleOpenVariantSelector('product_discount')}
                              >
                                اختيار المنتجات المستهدفة
                              </Button>
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                {selectedProductDiscountVariants.length > 0 || selectedProductDiscountCategories.length > 0
                                  ? `تم اختيار ${selectedProductDiscountVariants.length} منتج${selectedProductDiscountCategories.length > 0 ? ` و ${selectedProductDiscountCategories.length} تصنيف` : ''}`
                                  : 'لم يتم اختيار أي منتج أو تصنيف'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth variant="outlined">
                                <InputLabel>نوع الخصم</InputLabel>
                                <Field
                                  as={Select}
                                  name="productDiscountType"
                                  label="نوع الخصم"
                                >
                                  <MenuItem value="percentage">نسبة مئوية</MenuItem>
                                  <MenuItem value="fixed">قيمة ثابتة</MenuItem>
                                </Field>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                name="productDiscountValue"
                                label={`قيمة الخصم ${values.productDiscountType === 'percentage' ? '(%)' : ''}`}
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  inputProps: { min: 0, ...(values.productDiscountType === 'percentage' ? { max: 100 } : {}) },
                                  endAdornment: values.productDiscountType === 'percentage' ? (
                                    <InputAdornment position="end">%</InputAdornment>
                                  ) : null
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                as={TextField}
                                name="productDiscountMaxDiscount"
                                label="الحد الأقصى للخصم (0 = غير محدود)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </Grid>
                          </>
                        )}

                        {/* حقول آلية خصم على الضريبة */}
                        {values.schemeType === PromotionSchemeType.TAX_DISCOUNT && (
                          <>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                name="taxDiscountPercentage"
                                label="نسبة الخصم على الضريبة (%)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  inputProps: { min: 0, max: 100 },
                                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                name="taxDiscountMaxDiscount"
                                label="الحد الأقصى للخصم (0 = غير محدود)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => handleOpenVariantSelector('tax_discount')}
                              >
                                اختيار المنتجات المستهدفة لخصم الضريبة
                              </Button>
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                {selectedTaxDiscountVariants.length > 0 || selectedTaxDiscountCategories.length > 0
                                  ? `تم اختيار ${selectedTaxDiscountVariants.length} منتج${selectedTaxDiscountCategories.length > 0 ? ` و ${selectedTaxDiscountCategories.length} تصنيف` : ''}`
                                  : 'لم يتم اختيار أي منتج أو تصنيف'}
                              </Typography>
                            </Grid>
                          </>
                        )}

                        {/* حقول آلية خصم على الطلب */}
                        {values.schemeType === PromotionSchemeType.ORDER_DISCOUNT && (
                          <>
                            <Grid item xs={12}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => handleOpenVariantSelector('order_discount')}
                              >
                                اختيار المنتجات المستهدفة (اختياري)
                              </Button>
                              {selectedOrderDiscountVariants.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  {selectedOrderDiscountVariants.map((variant, index) => (
                                    <Chip
                                      key={index}
                                      label={`${variant.productName || 'منتج'} - ${variant.sku}`}
                                      onDelete={() => {
                                        const newVariants = selectedOrderDiscountVariants.filter((_, i) => i !== index);
                                        setSelectedOrderDiscountVariants(newVariants);
                                      }}
                                      sx={{ m: 0.5 }}
                                    />
                                  ))}
                                </Box>
                              )}
                              {selectedOrderDiscountCategories.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  {selectedOrderDiscountCategories.map((categoryId, index) => (
                                    <Chip
                                      key={index}
                                      label={`تصنيف: ${categoryId}`}
                                      onDelete={() => {
                                        const newCategories = selectedOrderDiscountCategories.filter((_, i) => i !== index);
                                        setSelectedOrderDiscountCategories(newCategories);
                                      }}
                                      sx={{ m: 0.5 }}
                                      color="secondary"
                                    />
                                  ))}
                                </Box>
                              )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                name="orderMinAmount"
                                label="الحد الأدنى لقيمة الطلب الإجمالي"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                name="orderMinTargetAmount"
                                label="الحد الأدنى لمجموع المنتجات المستهدفة"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{ inputProps: { min: 0 } }}
                                helperText="يُستخدم فقط عند تحديد منتجات مستهدفة"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth variant="outlined">
                                <InputLabel>نوع الخصم</InputLabel>
                                <Field
                                  as={Select}
                                  name="orderDiscountType"
                                  label="نوع الخصم"
                                >
                                  <MenuItem value="percentage">نسبة مئوية</MenuItem>
                                  <MenuItem value="fixed">قيمة ثابتة</MenuItem>
                                </Field>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                name="orderDiscountValue"
                                label={`قيمة الخصم ${values.orderDiscountType === 'percentage' ? '(%)' : ''}`}
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  inputProps: { min: 0, ...(values.orderDiscountType === 'percentage' ? { max: 100 } : {}) },
                                  endAdornment: values.orderDiscountType === 'percentage' ? (
                                    <InputAdornment position="end">%</InputAdornment>
                                  ) : null
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                as={TextField}
                                name="orderDiscountMaxDiscount"
                                label="الحد الأقصى للخصم (0 = غير محدود)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </Grid>
                          </>
                        )}

                        {/* حقول آلية الكومبو */}
                        {values.schemeType === PromotionSchemeType.COMBO && (
                          <>
                            <Grid item xs={12}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => handleOpenVariantSelector('combo')}
                              >
                                اختيار منتجات الكومبو
                              </Button>
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                {selectedComboVariants.length > 0 || selectedComboCategories.length > 0
                                  ? `تم اختيار ${selectedComboVariants.length} منتج${selectedComboCategories.length > 0 ? ` و ${selectedComboCategories.length} تصنيف` : ''}`
                                  : 'لم يتم اختيار أي منتج أو تصنيف'}
                              </Typography>
                            </Grid>

                            {/* إدارة خيارات الكومبو */}
                            <Grid item xs={12}>
                              <Typography variant="h6" gutterBottom>
                                خيارات الكومبو (العدد والسعر)
                              </Typography>
                              
                              {/* عرض الخيارات الحالية */}
                              {comboOptions.map((option, index) => (
                                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                                  <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        label="العدد"
                                        type="number"
                                        value={option.quantity}
                                        onChange={(e) => {
                                          const newOptions = [...comboOptions];
                                          newOptions[index].quantity = parseInt(e.target.value) || 2;
                                          setComboOptions(newOptions);
                                          setFieldValue('comboOptions', newOptions);
                                        }}
                                        InputProps={{ inputProps: { min: 2 } }}
                                        fullWidth
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        label="السعر الإجمالي"
                                        type="number"
                                        value={option.totalPrice}
                                        onChange={(e) => {
                                          const newOptions = [...comboOptions];
                                          newOptions[index].totalPrice = parseFloat(e.target.value) || 0;
                                          setComboOptions(newOptions);
                                          setFieldValue('comboOptions', newOptions);
                                        }}
                                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                        fullWidth
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        label="وصف (اختياري)"
                                        value={option.description || ''}
                                        onChange={(e) => {
                                          const newOptions = [...comboOptions];
                                          newOptions[index].description = e.target.value;
                                          setComboOptions(newOptions);
                                          setFieldValue('comboOptions', newOptions);
                                        }}
                                        fullWidth
                                        size="small"
                                        placeholder="مثال: اشتر 3 واحصل على خصم"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => {
                                          const newOptions = comboOptions.filter((_, i) => i !== index);
                                          setComboOptions(newOptions);
                                          setFieldValue('comboOptions', newOptions);
                                        }}
                                        fullWidth
                                      >
                                        حذف
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}

                              {/* زر إضافة خيار جديد */}
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  const newOption: ComboOption = {
                                    quantity: 2,
                                    totalPrice: 0,
                                    description: ''
                                  };
                                  const newOptions = [...comboOptions, newOption];
                                  setComboOptions(newOptions);
                                  setFieldValue('comboOptions', newOptions);
                                }}
                                sx={{ mb: 2 }}
                              >
                                إضافة خيار جديد
                              </Button>

                              {comboOptions.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  لم يتم إضافة أي خيارات للكومبو بعد. اضغط "إضافة خيار جديد" لبدء إضافة الخيارات.
                                </Typography>
                              )}
                            </Grid>
                          </>
                        )}

                        {/* حقول آلية السعر المستهدف */}
                        {values.schemeType === PromotionSchemeType.TARGET_PRICE && (
                          <>
                            <Grid item xs={12}>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => handleOpenVariantSelector('target')}
                              >
                                اختيار المنتجات المستهدفة
                              </Button>
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                {selectedTargetVariants.length > 0 || selectedTargetCategories.length > 0
                                  ? `تم اختيار ${selectedTargetVariants.length} منتج${selectedTargetCategories.length > 0 ? ` و ${selectedTargetCategories.length} تصنيف` : ''}`
                                  : 'لم يتم اختيار أي منتج أو تصنيف'}
                              </Typography>
                            </Grid>
                            
                            {/* مدير الأسعار المستهدفة */}
                            <Grid item xs={12}>
                              <TargetPriceManager
                                selectedVariants={selectedTargetVariants}
                                onTargetPricesChange={(targetPrices) => {
                                  console.log('Target prices changed:', targetPrices);
                                  setTargetPriceItems(targetPrices);
                                  setFieldValue('targetPriceItems', targetPrices);
                                }}
                                initialTargetPrices={values.targetPriceItems}
                                showOriginalPrice={values.showOriginalPrice}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.showOriginalPrice}
                                    onChange={(e) => setFieldValue('showOriginalPrice', e.target.checked)}
                                    color="primary"
                                  />
                                }
                                label="عرض السعر الأصلي"
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                {/* أزرار الحفظ والإلغاء */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/marketing/campaigns')}
                    >
                      إلغاء
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <CircularProgress size={24} /> : isEdit ? 'حفظ التغييرات' : 'إنشاء الحملة'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>

      {/* مربع حوار اختيار متغيرات المنتج */}
      <Dialog
        open={variantSelectorOpen}
        onClose={handleCloseVariantSelector}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>اختيار متغيرات المنتج</DialogTitle>
        <DialogContent>
          <ProductVariantSelector
            onSelect={handleSelectVariants}
            initialVariants={
              variantSelectorType === 'primary' ? selectedPrimaryVariants :
              variantSelectorType === 'secondary' ? selectedSecondaryVariants :
              variantSelectorType === 'combo' ? selectedComboVariants :
              variantSelectorType === 'target' ? selectedTargetVariants :
              variantSelectorType === 'product_discount' ? selectedProductDiscountVariants :
              variantSelectorType === 'tax_discount' ? selectedTaxDiscountVariants :
              variantSelectorType === 'order_discount' ? selectedOrderDiscountVariants :
              []
            }
            selectedCategories={
              variantSelectorType === 'primary' ? selectedPrimaryCategories :
              variantSelectorType === 'secondary' ? selectedSecondaryCategories :
              variantSelectorType === 'combo' ? selectedComboCategories :
              variantSelectorType === 'target' ? selectedTargetCategories :
              variantSelectorType === 'product_discount' ? selectedProductDiscountCategories :
              variantSelectorType === 'tax_discount' ? selectedTaxDiscountCategories :
              variantSelectorType === 'order_discount' ? selectedOrderDiscountCategories :
              selectedCategories
            }
            onCategoriesChange={(categories) => {
              switch (variantSelectorType) {
                case 'primary':
                  setSelectedPrimaryCategories(categories);
                  break;
                case 'secondary':
                  setSelectedSecondaryCategories(categories);
                  break;
                case 'combo':
                  setSelectedComboCategories(categories);
                  break;
                case 'target':
                  setSelectedTargetCategories(categories);
                  break;
                case 'product_discount':
                  setSelectedProductDiscountCategories(categories);
                  break;
                case 'tax_discount':
                  setSelectedTaxDiscountCategories(categories);
                  break;
                case 'order_discount':
                  setSelectedOrderDiscountCategories(categories);
                  break;
                default:
                  setSelectedCategories(categories);
              }
            }}
            onSave={(variants) => {
              handleSelectVariants(variants);
              handleCloseVariantSelector();
            }}
            onCancel={handleCloseVariantSelector}
            isTargetPriceCampaign={variantSelectorType === 'target'}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CampaignForm;